import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { siteConfig } from "../../config/siteConfig";

const RegisterModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const { register } = useAuth();
  const GOOGLE_TXT = {
    es: {
      btn: "Jatka Google-tilillä",
      or: "o",
      err: "Google-kirjautuminen epäonnistui. Yritä uudelleen.",
    },
    en: {
      btn: "Continue with Google",
      or: "or",
      err: "Could not sign in with Google. Please try again.",
    },
    de: {
      btn: "Weiter mit Google",
      or: "oder",
      err: "Anmeldung mit Google fehlgeschlagen. Bitte erneut versuchen.",
    },
    fr: {
      btn: "Continuer avec Google",
      or: "ou",
      err: "Connexion avec Google impossible. R\u00e9essayez.",
    },
    tr: {
      btn: "Google ile devam et",
      or: "veya",
      err: "Google ile giri\u015f yap\u0131lamad\u0131. L\u00fctfen tekrar deneyin.",
    },
  };
  const googleTxt =
    GOOGLE_TXT[(siteConfig.brand?.locale || "es").slice(0, 2)] || GOOGLE_TXT.es;
  const auth = siteConfig?.uiCopy?.auth || {};
  // Copies de l'écran de succès (config-driven, fallback sûr si la clé n'existe pas).
  const successTitle =
    auth?.registerSuccessTitle || "🎉 Rekisteröinti onnistui!";
  const successSubtitle =
    auth?.registerSuccessSubtitle || "Tilisi on luotu onnistuneesti";
  const successFallback = auth?.registerSuccessLabel || "Rekisteröinti valmis!";
  const successUserLabel =
    auth?.registerSuccessUserLabel || "Kirjaudu tunnuksellasi:";
  const successGoToLogin =
    auth?.registerSuccessGoToLogin || "Siirry kirjautumiseen";
  const successClose = auth?.registerSuccessClose || "Sulje";
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    acceptTerms: false,
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);
  const goToLoginRef = useRef(null);
  const panelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const ERROR_MESSAGES = {
    "User already registered": "Tällä sähköpostilla on jo tili.",
    default: "Rekisteröinnissä tapahtui virhe. Yritä uudelleen.",
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    // Validaciones
    if (!formData.fullName.trim()) {
      setError("Koko nimi on pakollinen");
      setIsLoading(false);
      return;
    }

    if (!formData.email.trim()) {
      setError("Sähköpostiosoite on pakollinen");
      setIsLoading(false);
      return;
    }

    // Validar formato de email (no solo confiar en type="email" del navegador)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setError("Anna kelvollinen sähköpostiosoite");
      setIsLoading(false);
      return;
    }

    // Teléfono: opcional, pero si se rellena debe parecer un número válido
    if (
      formData.phone.trim() &&
      !/^[+]?[\d\s().-]{6,20}$/.test(formData.phone.trim())
    ) {
      setError("Anna kelvollinen puhelinnumero tai jätä kenttä tyhjäksi");
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Salasanassa on oltava vähintään 6 merkkiä");
      setIsLoading(false);
      return;
    }

    if (!formData.confirmPassword) {
      setError("Vahvista salasanasi");
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Salasanat eivät täsmää");
      setIsLoading(false);
      return;
    }

    if (!formData.acceptTerms) {
      setError("Sinun on hyväksyttävä käyttöehdot");
      setIsLoading(false);
      return;
    }

    try {
      const result = await register(formData.email, formData.password, {
        name: formData.fullName,
        phone: formData.phone || "",
      });

      if (result && result.success === true) {
        setSuccessMessage(
          result.message ||
            "Rekisteröinti onnistui. Voit nyt kirjautua palveluun."
        );
        setIsRegistered(true);
        setIsLoading(false);
      } else {
        const rawMessage = result.error?.message || result.error;
        const translatedError =
          ERROR_MESSAGES[rawMessage] || ERROR_MESSAGES["default"];

        setError(translatedError);
        setIsLoading(false);
      }
    } catch (error) {
      setError(ERROR_MESSAGES["default"]);
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setIsLoading(false);
    setIsRegistered(false);
    setSuccessMessage("");
    setError("");
    setFormData({
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      acceptTerms: false,
    });
  };

  const handleSwitchToLogin = () => {
    onClose();
    onSwitchToLogin();
    setIsLoading(false);
    setIsRegistered(false);
    setSuccessMessage("");
    setError("");
    setFormData({
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      acceptTerms: false,
    });
  };

  const handleCloseToLanding = () => {
    onClose(); // Solo cierra el modal, App.jsx mostrará el landing automáticamente
    setIsLoading(false);
    setIsRegistered(false);
    setSuccessMessage("");
    setError("");
    setFormData({
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      acceptTerms: false,
    });
  };
  // Accesibilidad del diálogo modal: foco inicial, cierre con Escape,
  // focus trap (Tab/Shift+Tab) y restauración del foco al cerrar.
  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocusedRef.current = document.activeElement;

    const panel = panelRef.current;
    const getFocusable = () =>
      panel
        ? Array.from(
            panel.querySelectorAll(
              'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
            )
          )
        : [];

    const focusable = getFocusable();
    if (focusable.length > 0) {
      focusable[0].focus();
    } else if (panel) {
      panel.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCloseToLanding();
        return;
      }
      if (e.key === "Tab") {
        const items = getFocusable();
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey) {
          if (
            document.activeElement === first ||
            !panel.contains(document.activeElement)
          ) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (
            document.activeElement === last ||
            !panel.contains(document.activeElement)
          ) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === "function") {
        prev.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Al registrarse, mover el foco al botón principal de la pantalla de éxito
  useEffect(() => {
    if (isRegistered && goToLoginRef.current) {
      goToLoginRef.current.focus();
    }
  }, [isRegistered]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md animate-fade" />

      <div className="flex min-h-full items-start sm:items-center justify-center p-4 py-8">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="register-modal-title"
          className="relative w-full max-w-md transform overflow-hidden rounded-3xl bg-white elev-3 animate-slideUp"
        >
          {/* Barra superior - CAMBIA DE COLOR CUANDO REGISTRA */}
          <div
            className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r transition-all duration-500 ${
              isRegistered
                ? "from-emerald-500 via-green-500 to-teal-500"
                : "from-teal-500 via-cyan-500 to-emerald-500"
            }`}
          />
          <button
            onClick={handleCloseToLanding}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 transition-colors z-10"
            aria-label="Sulje ja palaa etusivulle"
          >
            <svg
              className="w-6 h-6 text-slate-400 hover:text-slate-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <div className="px-8 pt-10 pb-8">
            {/* Icono - CAMBIA CUANDO REGISTRA */}
            <div className="flex justify-center mb-6">
              <div
                className={`p-4 rounded-2xl elev-2 transition-all duration-500 transform ${
                  isRegistered
                    ? "bg-gradient-to-br from-emerald-500 to-green-600 scale-110 shadow-emerald-300"
                    : "bg-gradient-to-br from-teal-600 to-cyan-700 shadow-teal-200"
                }`}
              >
                {isRegistered ? (
                  <svg
                    className="w-10 h-10 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-5m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-10 h-10 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20c0-3.314 2.686-6 6-6h4c3.314 0 6 2.686 6 6v1H3v-1z"
                    />
                  </svg>
                )}
              </div>
            </div>

            {/* Título */}
            <h2
              id="register-modal-title"
              className="text-3xl font-black text-center text-slate-800 mb-2 transition-all duration-500"
            >
              {isRegistered ? successTitle : auth.registerTitle}
            </h2>
            <p className="text-center text-slate-500 mb-8">
              {isRegistered ? successSubtitle : auth.registerSubtitle}
            </p>

            {/* ========== PANTALLA DE ÉXITO ========== */}
            {isRegistered && (
              <div
                role="status"
                aria-live="polite"
                className="mb-6 p-6 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl animate-fade"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4 border-4 border-emerald-200">
                    <svg
                      className="w-10 h-10 text-emerald-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>

                  <span className="text-lg font-bold text-emerald-800 block mb-3">
                    {successMessage || successFallback}
                  </span>

                  <div className="bg-white/80 backdrop-blur-sm px-6 py-4 rounded-xl border border-emerald-200 w-full mb-6">
                    <p className="text-xs text-emerald-600 uppercase tracking-wider mb-1">
                      {successUserLabel}
                    </p>
                    <p className="text-sm font-bold bg-emerald-100 px-3 py-2 rounded-lg text-emerald-800 font-bold">
                      {formData.email}
                    </p>
                  </div>

                  <div className="w-full space-y-3 mt-2">
                    <button
                      ref={goToLoginRef}
                      onClick={handleSwitchToLogin}
                      className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold rounded-xl elev-2 shadow-emerald-200 transition-all duration-200 transform hover:-translate-y-0.5 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                    >
                      {successGoToLogin}
                    </button>
                    <button
                      onClick={handleClose}
                      className="w-full py-4 bg-white border-2 border-emerald-200 text-emerald-700 font-bold rounded-xl hover:bg-emerald-50 transition-all"
                    >
                      {successClose}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ========== MENSAJE DE ERROR ========== */}
            {error && !isRegistered && (
              <div
                role="alert"
                aria-live="assertive"
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-fade"
              >
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <span className="text-sm text-red-700 flex-1">{error}</span>
              </div>
            )}

            {/* ========== FORMULARIO DE REGISTRO ========== */}
            {!isRegistered && (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nombre completo */}
                <div>
                  <label
                    htmlFor="register-fullName"
                    className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"
                  >
                    Koko nimi
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-400">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </span>
                    <input
                      id="register-fullName"
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Koko nimesi"
                      disabled={isLoading}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="register-email"
                    className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"
                  >
                    Sähköpostiosoite
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-400">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </span>
                    <input
                      id="register-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="sinun@sähköposti.fi"
                      disabled={isLoading}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      required
                    />
                  </div>
                </div>

                {/* Teléfono */}
                <div>
                  <label
                    htmlFor="register-phone"
                    className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"
                  >
                    Puhelin{" "}
                    <span className="text-slate-300">(valinnainen)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-400">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    </span>
                    <input
                      id="register-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={siteConfig.niche.phonePlaceholder}
                      disabled={isLoading}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Contraseña */}
                <div>
                  <label
                    htmlFor="register-password"
                    className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"
                  >
                    Salasana
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-400">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </span>
                    <input
                      id="register-password"
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      disabled={isLoading}
                      aria-describedby="register-password-hint"
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      required
                    />
                  </div>
                  <p
                    id="register-password-hint"
                    className="mt-1.5 text-xs text-slate-400"
                  >
                    Vähintään 6 merkkiä
                  </p>
                </div>

                {/* Vahvista salasana */}
                <div>
                  <label
                    htmlFor="register-confirmPassword"
                    className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2"
                  >
                    Vahvista salasana
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-400">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-5m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                    </span>
                    <input
                      id="register-confirmPassword"
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="••••••••"
                      disabled={isLoading}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      required
                    />
                  </div>
                </div>

                {/* Términos */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    disabled={isLoading}
                    className="mt-1 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className="text-xs text-slate-600 group-hover:text-slate-800 transition-colors">
                    Hyväksyn{" "}
                    <a
                      href="/legal?tab=terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-teal-600 hover:text-teal-800 underline decoration-teal-200 underline-offset-2"
                    >
                      Käyttöehdot
                    </a>{" "}
                    y la{" "}
                    <a
                      href="/legal?tab=privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-teal-600 hover:text-teal-800 underline decoration-teal-200 underline-offset-2"
                    >
                      Tietosuojaseloste
                    </a>
                  </span>
                </label>

                {/* Botón submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full py-4 text-white font-bold rounded-2xl transition-all duration-200 transform hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <span
                        className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"
                        aria-hidden="true"
                      />
                      Luodaan tiliä...
                    </>
                  ) : (
                    "Rekisteröidy"
                  )}
                </button>
              </form>
            )}
          </div>
          {/* Login */}
          <p className="text-center text-sm text-slate-600 pb-8">
            Oletko jo rekisteröitynyt?{" "}
            <button
              onClick={handleSwitchToLogin}
              className="font-bold text-teal-600 hover:text-teal-800 transition-colors"
            >
              Kirjaudu sisään
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterModal;
